/**
 * One recommendation as a register row reads it: its number, the investigation that issued it,
 * what marks it out (urgent, superseded, reiterated), the recommendation itself, and why it stands
 * where it does. Shared by the workbook's register and the Safety Recommendations register.
 */

import React from 'react';
import {Box, ButtonBase, Chip, Link, Stack, Tooltip, Typography} from '@mui/material';
import {alpha, Theme} from '@mui/material/styles';
import {SystemStyleObject} from '@mui/system';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    ChunkPart,
    SafetyRecommendationDTO,
    NtsbMitigationTaxonomyTagDTO,
    NtsbMitigationTaxonomyTagOrigin,
} from '../../@types/safety-recommendation';
import {segmentText} from '../common/HighlightedNarrative.component';
import {STATUS_STYLE} from './RecommendationOverview';

/** Where a passage matched by meaning came from, in the words a reader would use. */
const MATCHED_IN_LABEL: Record<ChunkPart, string> = {
    ASK: 'Matched in the recommendation',
    ACCIDENT_SUMMARY: 'Matched in the accident summary',
    REPORT_NARRATIVE: 'Matched in the report narrative',
};

/**
 * The typed words, marked where they appear. Only literal matches highlight: an embedding has no
 * term-level attribution, so a row matched by meaning gets its winning passage instead. The
 * splitting is the report narrative's, so both views mark up the same runs of text.
 */
const highlight = (text: string, terms: string[]): React.ReactNode => {
    const words = terms.map((term) => term.trim()).filter((term) => term.length > 0);
    if (words.length === 0) {
        return text;
    }
    return segmentText(text, words).map((segment, index) => (
        segment.highlight
            ? <mark key={index} style={{backgroundColor: 'rgba(250, 178, 25, 0.35)', padding: 0}}>{segment.text}</mark>
            : <React.Fragment key={index}>{segment.text}</React.Fragment>
    ));
};

/**
 * A colour per taxonomy category, so a row can be read at a glance: equipment, people, procedures
 * and management are different kinds of answer to an accident, and the colour says which without
 * the reader parsing the words.
 */
export const CATEGORY_COLOUR: Record<string, 'info' | 'success' | 'warning' | 'secondary'> = {
    'Aircraft / Equipment / Facilities': 'info',
    'Personnel': 'success',
    'Procedures / Regulations': 'warning',
    'Quality / Safety Management': 'secondary',
};

/**
 * One chip per classification, the two origins merged.
 *
 * <p>A classification both the issuer and our tagging chose is one chip, not two: the reader cares
 * that it is agreed, not that two rows exist. The tooltip is where the origin is spelled out.</p>
 */
const taxonomyChips = (tags: NtsbMitigationTaxonomyTagDTO[] | undefined):
        {leafId: number; label: string; title: string; fromIssuer: boolean;
            colour: 'info' | 'success' | 'warning' | 'secondary' | 'default'}[] => {
    const byLeaf = new Map<number, {leaf: string; category: string; origins: Set<NtsbMitigationTaxonomyTagOrigin>}>();
    (tags ?? []).forEach((tag) => {
        const held = byLeaf.get(tag.leafId);
        if (held) {
            held.origins.add(tag.origin);
            return;
        }
        byLeaf.set(tag.leafId, {leaf: tag.leaf, category: tag.category, origins: new Set([tag.origin])});
    });
    return Array.from(byLeaf.entries()).map(([leafId, held]) => {
        const fromIssuer = held.origins.has('NTSB');
        const fromModel = held.origins.has('MODEL');
        const source = fromIssuer && fromModel
            ? 'Tagged by the NTSB, and by ISAM'
            : fromIssuer ? 'Tagged by the NTSB' : 'Tagged by ISAM';
        return {
            leafId,
            label: held.leaf,
            title: `${source}. Category: ${held.category}`,
            fromIssuer,
            colour: CATEGORY_COLOUR[held.category] ?? 'default',
        };
    });
};

interface AssessmentLineProps {
    rec: SafetyRecommendationDTO;
    onOpen: () => void;
}

/**
 * Why the recommendation stands where it does: an excerpt of the issuer's assessment letter, marked
 * with the status colour and icon. Opens the full correspondence.
 */
const AssessmentLine: React.FC<AssessmentLineProps> = ({rec, onOpen}) => {
    const {color, Icon} = STATUS_STYLE[rec.focusStatusGroup];
    return (
        <Tooltip title="Open every letter between the NTSB and the addressees, oldest first" enterDelay={600}>
            <ButtonBase
                onClick={onOpen}
                aria-label={`Why: read the ${rec.number} correspondence`}
                sx={(theme): SystemStyleObject<Theme> => ({
                    mt: 0.75, width: '100%', display: 'flex', alignItems: 'stretch', justifyContent: 'flex-start', gap: 1,
                    textAlign: 'left', px: 1, py: 0.75, borderRadius: '0 4px 4px 0',
                    borderLeft: `3px solid ${color}`, outline: '1px solid transparent', outlineOffset: -1,
                    backgroundColor: alpha(color, 0.06),
                    transition: 'background-color 120ms, outline-color 120ms',
                    '&:hover, &.Mui-focusVisible': {
                        backgroundColor: alpha(color, 0.13),
                        outlineColor: alpha(theme.palette.primary.main, 0.5),
                        '& .ntsb-read-letters': {textDecoration: 'underline'},
                        '& .ntsb-chevron': {transform: 'translateX(2px)'},
                    },
                })}
            >
                <Icon sx={{fontSize: 16, color, mt: 0.25, flexShrink: 0}} />
                <Box sx={{flex: 1, minWidth: 0}}>
                    {rec.assessmentExcerpt ? (
                        <Typography variant="body2" component="span" sx={{
                            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                            <Box component="span" sx={{fontWeight: 600}}>NTSB, {rec.assessmentDate}: </Box>
                            {rec.assessmentExcerpt}
                        </Typography>
                    ) : (
                        <Typography variant="body2" component="span" color="text.secondary">
                            No NTSB assessment letter on record.
                        </Typography>
                    )}
                    <Typography
                        component="span"
                        variant="body2"
                        color="primary"
                        className="ntsb-read-letters"
                        sx={{mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600}}
                    >
                        <ForumOutlinedIcon sx={{fontSize: 16}} />
                        Read the full correspondence
                    </Typography>
                </Box>
                <ChevronRightIcon className="ntsb-chevron" color="primary"
                    sx={{alignSelf: 'center', flexShrink: 0, transition: 'transform 120ms'}} />
            </ButtonBase>
        </Tooltip>
    );
};

interface RecommendationSummaryProps {
    rec: SafetyRecommendationDTO;
    /** The words typed, marked wherever they appear in the number and the text. */
    searchTerms?: string[];
    /** Opens the full correspondence, from the number's status line and from the why line. */
    onOpenLetters: () => void;
    /** When given, the number opens the recommendation's own tab instead of the source's letter. */
    onOpen?: () => void;
}

const RecommendationSummary: React.FC<RecommendationSummaryProps> = ({rec, onOpenLetters, onOpen, searchTerms = []}) => (
    <>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            {onOpen ? (
                <Tooltip title="Open this recommendation">
                    <Link
                        component="button"
                        type="button"
                        variant="body2"
                        fontWeight={600}
                        onClick={onOpen}
                        aria-label={`Open ${rec.number}`}
                    >
                        {rec.number}
                    </Link>
                </Tooltip>
            ) : rec.letterLink ? (
                <Tooltip title="Open the NTSB recommendation letter (PDF)">
                    <Link
                        href={rec.letterLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        fontWeight={600}
                        aria-label={`${rec.number} recommendation letter`}
                        sx={{display: 'inline-flex', alignItems: 'center', gap: 0.25}}
                    >
                        {rec.number}
                        <OpenInNewIcon sx={{fontSize: 14}} />
                    </Link>
                </Tooltip>
            ) : (
                <Typography variant="body2" fontWeight={600}>{rec.number}</Typography>
            )}
            <Typography variant="caption" color="text.secondary">
                {rec.caseLink && rec.issuingCaseNumber ? (
                    <Tooltip title="Open the NTSB investigation page">
                        <Link href={rec.caseLink} target="_blank" rel="noopener noreferrer" color="inherit">
                            {rec.issuingCaseNumber}
                        </Link>
                    </Tooltip>
                ) : rec.issuingCaseNumber}
                {[rec.eventDate, rec.eventLocation].filter(Boolean).map((part) => ` · ${part}`).join('')}
            </Typography>
            {rec.priority === 'CLASS I' && <Chip size="small" color="error" label="Urgent" sx={{height: 18}} />}
            {rec.superseded && (
                <Chip size="small" variant="outlined" sx={{height: 18}}
                    label={`Superseded by ${rec.supersededBy.map((s) => s.number).join(', ')}`} />
            )}
            {!!rec.timesReiterated && (
                <Chip size="small" variant="outlined" sx={{height: 18}} label={`Reiterated ${rec.timesReiterated}x`} />
            )}
            {/* Where this sits in the taxonomy. Colour is the category; the issuer's own coding is
            filled and ours is outlined, so the two claims stay distinguishable ("the NTSB coded
            this" and "we think this" are not the same thing). A classification both chose is shown
            once, marked as both in its tooltip. */}
            {taxonomyChips(rec.taxonomyTags).map((chip) => (
                <Tooltip describeChild key={chip.leafId} title={chip.title}>
                    <Chip
                        size="small"
                        color={chip.colour}
                        variant={chip.fromIssuer ? 'filled' : 'outlined'}
                        sx={{height: 18}}
                        label={chip.label}
                    />
                </Tooltip>
            ))}
            {/* describeChild, so the chip is still called "by meaning": a plain Tooltip would take
            the tip's wording as the chip's name and leave it with none of its own. */}
            {rec.matchedBy === 'MEANING' && (
                <Tooltip describeChild title={`Found by meaning rather than by your words${
                    rec.score === undefined ? '' : `, ${(rec.score * 100).toFixed(0)}% close`}`}>
                    <Chip size="small" variant="outlined" color="primary" sx={{height: 18}} label="by meaning" />
                </Tooltip>
            )}
        </Stack>
        <Typography variant="body2" sx={{mt: 0.5, whiteSpace: 'pre-line'}}>
            {highlight(rec.text, searchTerms)}
        </Typography>
        {rec.matchedBy === 'MEANING' && rec.matchedSnippet && (
            <Typography variant="body2" color="text.secondary" sx={{
                mt: 0.5, pl: 1, borderLeft: 3, borderColor: 'primary.light',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
                <Box component="span" sx={{fontWeight: 600}}>
                    {`${rec.matchedIn ? MATCHED_IN_LABEL[rec.matchedIn] : 'Matched'}: `}
                </Box>
                {rec.matchedSnippet}
            </Typography>
        )}
        <AssessmentLine rec={rec} onOpen={onOpenLetters} />
    </>
);

export default RecommendationSummary;
